import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import {
  FaTimes,
  FaClipboardList,
  FaHospital,
  FaUserInjured,
  FaCalendarCheck,
  FaHome,
} from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import styles from './ProjectDetailModal.module.css';

// Hasta durumları - PatientsModal ile aynı sözlük
export const PATIENT_STATUSES = {
  planned: { label: 'Planlanan', color: '#f59e0b' },
  screened: { label: 'Screen Edilen', color: '#3b82f6' },
  screenFail: { label: 'Screen Fail', color: '#ef4444' },
  enrolled: { label: 'Dahil Edilen', color: '#8b5cf6' },
  active: { label: 'Aktif', color: '#10b981' },
  onMedication: { label: 'İlaç Alan', color: '#06b6d4' },
  completed: { label: 'Tamamlanan', color: '#6b7280' },
  withdrawn: { label: 'Çekilen', color: '#dc2626' },
};

export const formatDate = (value) => {
  if (!value) return '-';
  if (value.seconds) return new Date(value.seconds * 1000).toLocaleDateString('tr-TR');
  if (typeof value.toDate === 'function') return value.toDate().toLocaleDateString('tr-TR');
  const date = new Date(value);
  return isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('tr-TR');
};

// Bir projeye ait hasta özetini hesaplar (Excel çıktısında da kullanılıyor)
export const buildPatientSummary = (patients) => {
  const byStatus = {};
  Object.keys(PATIENT_STATUSES).forEach((key) => {
    byStatus[key] = 0;
  });
  let homeVisit = 0;
  patients.forEach((patient) => {
    if (patient.status && byStatus[patient.status] !== undefined) {
      byStatus[patient.status] += 1;
    }
    if (patient.hasHomeVisit) homeVisit += 1;
  });
  return { total: patients.length, byStatus, homeVisit };
};

const ProjectDetailModal = ({ project, assignments, employee, isAdmin, onClose }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [patientSummary, setPatientSummary] = useState(null);
  const [visitCounts, setVisitCounts] = useState({ project: 0, employee: 0 });
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;

    const fetchSummary = async () => {
      setLoading(true);
      setError(null);
      try {
        const [patientsSnap, visitsSnap] = await Promise.all([
          getDocs(query(collection(db, 'Patients'), where('projectId', '==', project.id))),
          getDocs(query(collection(db, 'Visits'), where('projectId', '==', project.id))),
        ]);
        if (cancelled) return;

        const patients = patientsSnap.docs.map((d) => d.data());
        const visits = visitsSnap.docs.map((d) => d.data());

        setPatientSummary(buildPatientSummary(patients));
        setVisitCounts({
          project: visits.length,
          employee: visits.filter((v) => v.employeeId === employee.id).length,
        });
      } catch (err) {
        console.error('Error fetching project summary:', err);
        if (!cancelled) setError('Proje özeti yüklenirken bir hata oluştu.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSummary();
    return () => {
      cancelled = true;
    };
  }, [project.id, employee.id]);

  // Çalışanın bu projede görevli olduğu merkezler
  const centerRows = useMemo(() => {
    const rows = new Map();
    assignments.forEach((assignment) => {
      const key = assignment.centerId || assignment.centerName || '-';
      const existing = rows.get(key);
      if (existing) {
        if (assignment.role && !existing.roles.includes(assignment.role)) {
          existing.roles.push(assignment.role);
        }
        if (assignment.status !== 'passive') existing.status = 'active';
      } else {
        rows.set(key, {
          key,
          centerName: assignment.centerName || 'Belirtilmemiş',
          roles: assignment.role ? [assignment.role] : [],
          workerType: assignment.workerType || '-',
          blindStatus: assignment.blindStatus || '-',
          assignedAt: assignment.assignedAt,
          rate: assignment.rate,
          status: assignment.status === 'passive' ? 'passive' : 'active',
        });
      }
    });
    return Array.from(rows.values());
  }, [assignments]);

  const hasActiveAssignment = centerRows.some((row) => row.status === 'active');

  const formatRate = useCallback((rate) => {
    if (!rate) return '-';
    const type = rate.type?.toUpperCase() === 'FTE' ? 'FTE' : 'Saatlik';
    const amount = rate.value ?? rate.price;
    if (amount === undefined || amount === null || amount === '') return type;
    return `${type}: ${amount}${rate.currency ? ` ${rate.currency}` : ''}`;
  }, []);

  const statusBreakdown = useMemo(() => {
    if (!patientSummary) return [];
    return Object.entries(PATIENT_STATUSES).map(([key, meta]) => ({
      key,
      ...meta,
      count: patientSummary.byStatus[key] || 0,
    }));
  }, [patientSummary]);

  return (
    <div
      className={styles.modalOverlay}
      onClick={onClose}
      data-theme={isDarkMode ? 'dark' : 'light'}
    >
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.headerText}>
            <h2 className={styles.modalTitle}>
              <FaClipboardList />
              {project.titleShort || 'Proje Detayı'}
            </h2>
            <p className={styles.subtitle} title={project.titleLong}>
              {project.titleLong || '-'}
            </p>
          </div>
          <button className={styles.closeButton} onClick={onClose} aria-label="Kapat">
            <FaTimes />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Protokol Numarası</span>
              <span className={styles.infoValue}>{project.protocolNumber || '-'}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Müşteri</span>
              <span className={styles.infoValue}>{project.customer || '-'}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Proje Tarihi</span>
              <span className={styles.infoValue}>{formatDate(project.projectDate)}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Proje Bitiş Tarihi</span>
              <span className={styles.infoValue}>{formatDate(project.projectDueDate)}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Proje Durumu</span>
              <span
                className={`${styles.badge} ${
                  (project.projectStatus || 'Aktif') === 'Aktif'
                    ? styles.badgeActive
                    : styles.badgePassive
                }`}
              >
                {project.projectStatus || 'Aktif'}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Görev Durumu</span>
              <span
                className={`${styles.badge} ${
                  hasActiveAssignment ? styles.badgeActive : styles.badgePassive
                }`}
              >
                {hasActiveAssignment ? 'Aktif' : 'Pasif'}
              </span>
            </div>
          </div>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <FaHospital />
              Merkezler ve Görevler ({centerRows.length})
            </h3>
            {centerRows.length === 0 ? (
              <p className={styles.emptyMessage}>
                Bu çalışan için merkez bazlı görev kaydı bulunmamaktadır.
              </p>
            ) : (
              <div className={styles.tableContainer}>
                <table className={styles.detailTable}>
                  <thead>
                    <tr>
                      <th>Merkez</th>
                      <th>Rol</th>
                      <th>Çalışan Tipi</th>
                      <th>Körleme</th>
                      <th>Atanma Tarihi</th>
                      {isAdmin && <th>Ücret</th>}
                      <th>Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {centerRows.map((row) => (
                      <tr key={row.key}>
                        <td>{row.centerName}</td>
                        <td>{row.roles.length ? row.roles.join(', ') : '-'}</td>
                        <td>{row.workerType}</td>
                        <td>{row.blindStatus}</td>
                        <td>{formatDate(row.assignedAt)}</td>
                        {isAdmin && <td>{formatRate(row.rate)}</td>}
                        <td>
                          <span
                            className={`${styles.badge} ${
                              row.status === 'active' ? styles.badgeActive : styles.badgePassive
                            }`}
                          >
                            {row.status === 'active' ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <FaUserInjured />
              Hasta ve Ziyaret Özeti
            </h3>
            {loading ? (
              <p className={styles.emptyMessage}>Özet yükleniyor...</p>
            ) : error ? (
              <p className={styles.errorMessage}>{error}</p>
            ) : (
              <>
                <div className={styles.statCards}>
                  <div className={styles.statCard}>
                    <span className={styles.statLabel}>Toplam Hasta</span>
                    <span className={styles.statValue}>{patientSummary?.total ?? 0}</span>
                  </div>
                  <div className={styles.statCard}>
                    <span className={styles.statLabel}>
                      <FaHome /> Ev Ziyaretli Hasta
                    </span>
                    <span className={styles.statValue}>{patientSummary?.homeVisit ?? 0}</span>
                  </div>
                  <div className={styles.statCard}>
                    <span className={styles.statLabel}>
                      <FaCalendarCheck /> Toplam Ziyaret
                    </span>
                    <span className={styles.statValue}>{visitCounts.project}</span>
                  </div>
                  <div className={styles.statCard}>
                    <span className={styles.statLabel}>
                      <FaCalendarCheck /> Çalışanın Ziyareti
                    </span>
                    <span className={styles.statValue}>{visitCounts.employee}</span>
                  </div>
                </div>

                <div className={styles.statusGrid}>
                  {statusBreakdown.map((status) => (
                    <div key={status.key} className={styles.statusItem}>
                      <span className={styles.statusDot} style={{ backgroundColor: status.color }} />
                      <span className={styles.statusLabel}>{status.label}</span>
                      <span className={styles.statusCount}>{status.count}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailModal;
